import { useThree } from '@react-three/fiber'
import React from 'react'
import {
  DefaultLoadingManager,
  LoadingManager,
  type WebGLRenderer,
} from 'three'
import { GLTFLoader, KTX2Loader } from 'three-stdlib'

/**
 * Basis Universal transcoder bundled with this module (see libs/basis/README).
 * `new URL(..., import.meta.url)` is understood by both Vite and webpack 5, so
 * the assets ship with whichever host app consumes this submodule.
 */
const TRANSCODER_JS_URL = new URL(
  '../libs/basis/basis_transcoder.js.txt',
  import.meta.url
).href
const TRANSCODER_WASM_URL = new URL(
  '../libs/basis/basis_transcoder.wasm',
  import.meta.url
).href

/**
 * KTX2Loader always requests `<transcoderPath>basis_transcoder.js|.wasm`.
 * Bundlers hash asset filenames, so instead of a directory we redirect those
 * two fixed names to the bundled URLs via the LoadingManager's URL modifier.
 * Item events are forwarded to the DefaultLoadingManager so drei's
 * `useProgress` keeps counting KTX2 textures.
 */
function createManager(): LoadingManager {
  const manager = new LoadingManager()
  manager.setURLModifier((url) => {
    if (url.endsWith('basis_transcoder.js')) return TRANSCODER_JS_URL
    if (url.endsWith('basis_transcoder.wasm')) return TRANSCODER_WASM_URL
    return url
  })
  manager.itemStart = (url) => DefaultLoadingManager.itemStart(url)
  manager.itemEnd = (url) => DefaultLoadingManager.itemEnd(url)
  manager.itemError = (url) => DefaultLoadingManager.itemError(url)
  return manager
}

let ktx2Loader: KTX2Loader | null = null
let detectedRenderer: WebGLRenderer | null = null

/**
 * Returns a shared KTX2Loader. Multiple KTX2Loader instances each spin up
 * their own worker pool, so a singleton is used across all models.
 * `detectSupport` needs the live renderer to pick the GPU compressed format,
 * so it's re-run whenever the renderer changes (e.g. canvas remount).
 */
export function getKTX2Loader(gl: WebGLRenderer): KTX2Loader {
  if (!ktx2Loader) {
    ktx2Loader = new KTX2Loader(createManager()).setTranscoderPath('')
  }
  if (detectedRenderer !== gl) {
    ktx2Loader.detectSupport(gl)
    detectedRenderer = gl
  }
  return ktx2Loader
}

/**
 * Hook returning an `extendLoader` callback for drei's `useGLTF`, so glTF
 * files using KHR_texture_basisu (.ktx2 textures) can be decoded.
 *
 * Must be called inside a `<Canvas>` (needs the renderer).
 */
export function useKTX2Extend(): (loader: GLTFLoader) => void {
  const gl = useThree((state) => state.gl)
  const loader = React.useMemo(() => getKTX2Loader(gl), [gl])
  return React.useCallback(
    (gltfLoader: GLTFLoader) => {
      gltfLoader.setKTX2Loader(loader)
    },
    [loader]
  )
}
